(function () {
  const ROOT_SELECTOR = '[data-user-portal-session-root]';
  const ACCOUNT_TRIGGER_SELECTOR = '[data-account-trigger]';
  const ACCOUNT_MENU_SELECTOR = '[data-account-menu]';
  const USER_ID_STORAGE_KEY = 'itmart24_user_portal_user_id';
  const USER_INITIALS_STORAGE_KEY = 'itmart24_user_portal_user_initials';
  const ACCOUNT_TYPE_STORAGE_KEY = 'itmart24_portal_account_type';
  const ACCOUNT_CLICK_ENABLED_STORAGE_KEY = 'itmart24_user_portal_account_click_enabled';
  const PUBLIC_SESSION_COOKIE_KEY = 'itmart24_user_portal_public_session';
  const PUBLIC_SETTINGS_PATH = '/users/settings/public/header-account-icon';
  const USER_ACCOUNT_TYPE = 'user';
  const VENDOR_ACCOUNT_TYPE = 'vendor';
  const GUEST_ACCOUNT_TYPE = 'guest';

  function buildInitials(input) {
    const source = String(input || '').trim();
    if (!source) return '';

    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }

  function getPortalBase(root) {
    return String(root?.dataset?.userPortalBase || 'https://user.itmart24.com').replace(/\/$/, '');
  }

  function getVendorPortalBase(root) {
    return String(root?.dataset?.vendorPortalBase || 'https://vendor.itmart24.com').replace(/\/$/, '');
  }

  function readCookie(name) {
    const escapedName = String(name || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function parsePublicSessionCookie() {
    const encoded = readCookie(PUBLIC_SESSION_COOKIE_KEY);
    if (!encoded) return null;

    try {
      const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized + '='.repeat((4 - (normalized.length % 4 || 4)) % 4);
      const parsed = JSON.parse(window.atob(padded));

      return {
        userId: String(parsed?.userId || '').trim(),
        initials: String(parsed?.initials || '').trim(),
      };
    } catch (_error) {
      return null;
    }
  }

  function getStoredUserId() {
    return window.sessionStorage.getItem(USER_ID_STORAGE_KEY) || '';
  }

  function getStoredInitials() {
    return window.sessionStorage.getItem(USER_INITIALS_STORAGE_KEY) || '';
  }

  function getStoredAccountType() {
    return window.sessionStorage.getItem(ACCOUNT_TYPE_STORAGE_KEY) || '';
  }

  function getSessionState(root) {
    const publicSession = parsePublicSessionCookie();
    const vendorSession = root?.dataset?.vendorPortalSession === 'live'
      || String(root?.dataset?.vendorPortalVendorId || '').trim()
      || String(root?.dataset?.vendorPortalVendorInitials || '').trim();

    if (!root) {
      return {
        loggedIn: false,
        accountType: GUEST_ACCOUNT_TYPE,
        userId: '',
        initials: '',
      };
    }

    if (vendorSession) {
      return {
        loggedIn: true,
        accountType: VENDOR_ACCOUNT_TYPE,
        userId: String(root.dataset.vendorPortalVendorId || '').trim(),
        initials: String(root.dataset.vendorPortalVendorInitials || '').trim(),
      };
    }

    if (root.dataset.userPortalSession === 'live' || String(root.dataset.userPortalUserId || '').trim()) {
      return {
        loggedIn: true,
        accountType: USER_ACCOUNT_TYPE,
        userId: String(root.dataset.userPortalUserId || '').trim(),
        initials: String(root.dataset.userPortalUserInitials || '').trim(),
      };
    }

    if (publicSession?.userId || publicSession?.initials) {
      return {
        loggedIn: true,
        accountType: USER_ACCOUNT_TYPE,
        userId: publicSession.userId,
        initials: publicSession.initials,
      };
    }

    const storedUserId = getStoredUserId();
    const storedInitials = getStoredInitials();

    if (storedUserId || storedInitials) {
      return {
        loggedIn: true,
        accountType: getStoredAccountType() || USER_ACCOUNT_TYPE,
        userId: storedUserId,
        initials: storedInitials,
      };
    }

    return {
      loggedIn: false,
      accountType: GUEST_ACCOUNT_TYPE,
      userId: '',
      initials: '',
    };
  }

  function isLoggedIn(root) {
    return getSessionState(root).loggedIn;
  }

  function getAccountTargetHref(root, sessionState) {
    if (!sessionState?.loggedIn) {
      return `${getPortalBase(root)}/login`;
    }

    if (sessionState.accountType === VENDOR_ACCOUNT_TYPE) {
      return `${getVendorPortalBase(root)}/dashboard`;
    }

    return `${getPortalBase(root)}/dashboard`;
  }

  function getEffectiveClickEnabled(root) {
    return isLoggedIn(root) ? true : isAccountClickEnabled(root);
  }

  function applyAccountClickState(root, clickEnabled, isLoggedIn) {
    const accountLinks = document.querySelectorAll(ACCOUNT_TRIGGER_SELECTOR);
    const sessionState = getSessionState(root);

    accountLinks.forEach((link) => {
      const iconWrapper = link.querySelector('.svg-wrapper');
      const targetHref = getAccountTargetHref(root, sessionState);

      link.setAttribute('data-user-portal-account-href', targetHref);

      if (clickEnabled) {
        link.style.cursor = '';
        link.removeAttribute('tabindex');
      } else {
        link.style.cursor = 'default';
        link.setAttribute('tabindex', '-1');
      }

      link.dataset.userPortalClickEnabled = clickEnabled ? 'true' : 'false';
      link.setAttribute('aria-disabled', clickEnabled ? 'false' : 'true');

      if (!clickEnabled && iconWrapper) {
        iconWrapper.style.display = '';
      }
    });
  }

  function updateAccountMenuState(root, sessionState, clickEnabled) {
    const accountMenus = document.querySelectorAll(ACCOUNT_MENU_SELECTOR);
    const labelText = sessionState.loggedIn
      ? sessionState.accountType === VENDOR_ACCOUNT_TYPE
        ? 'Vendor Account'
        : 'My Account'
      : 'Login';

    accountMenus.forEach((menu) => {
      menu.dataset.accountMenuMode = !clickEnabled
        ? 'disabled'
        : sessionState.loggedIn
          ? 'account'
          : 'guest';

      if (menu.open && menu.dataset.accountMenuMode !== 'guest') {
        menu.open = false;
      }
    });

    document.querySelectorAll('[data-account-trigger-label]').forEach((label) => {
      label.textContent = labelText;
    });
  }

  function renderAccountState(root, initials, clickEnabled) {
    const accountLinks = document.querySelectorAll(ACCOUNT_TRIGGER_SELECTOR);
    const sessionState = getSessionState(root);
    const loggedIn = sessionState.loggedIn;
    const sessionInitials = sessionState.accountType === VENDOR_ACCOUNT_TYPE
      ? root?.dataset?.vendorPortalVendorInitials
      : root?.dataset?.userPortalUserInitials;
    const normalizedInitials = String(initials || sessionInitials || getStoredInitials()).trim();
    const effectiveClickEnabled = loggedIn ? true : clickEnabled;
    applyAccountClickState(root, effectiveClickEnabled, loggedIn);
    updateAccountMenuState(root, sessionState, effectiveClickEnabled);

    accountLinks.forEach((link) => {
      const existingBadge = link.querySelector('[data-user-portal-account-badge]');
      const iconWrapper = link.querySelector('.svg-wrapper');
      const accountIcon = link.querySelector('account-icon');

      if (normalizedInitials && loggedIn) {
        if (iconWrapper) {
          iconWrapper.style.display = 'none';
        }

        if (accountIcon && !accountIcon.querySelector('[data-user-portal-account-badge]')) {
          const badge = document.createElement('span');
          badge.setAttribute('data-user-portal-account-badge', 'true');
          badge.className = 'user-portal-account-badge';
          badge.textContent = normalizedInitials;
          accountIcon.appendChild(badge);
        } else if (!accountIcon && !existingBadge) {
          const badge = document.createElement('span');
          badge.setAttribute('data-user-portal-account-badge', 'true');
          badge.className = 'user-portal-account-badge';
          badge.textContent = normalizedInitials;
          link.insertBefore(badge, link.firstChild);
        } else if (existingBadge) {
          existingBadge.textContent = normalizedInitials;
        }
        return;
      }

      if (iconWrapper) {
        iconWrapper.style.display = '';
      }

      if (existingBadge) {
        existingBadge.remove();
      }
    });
  }

  function isAccountClickEnabled(root) {
    return root.dataset.userPortalAccountClickEnabled !== 'false';
  }

  function syncStoredSessionToRoot(root) {
    const publicSession = parsePublicSessionCookie();
    const storedUserId = getStoredUserId();
    const storedInitials = getStoredInitials();
    const storedAccountType = getStoredAccountType() || USER_ACCOUNT_TYPE;
    const effectiveUserId = publicSession?.userId || storedUserId;
    const effectiveInitials = publicSession?.initials || storedInitials;

    if (effectiveUserId) {
      root.dataset.userPortalUserId = effectiveUserId;
    } else {
      delete root.dataset.userPortalUserId;
    }

    if (effectiveInitials) {
      root.dataset.userPortalUserInitials = effectiveInitials;
    } else {
      delete root.dataset.userPortalUserInitials;
    }

    if (effectiveUserId) {
      window.sessionStorage.setItem(USER_ID_STORAGE_KEY, effectiveUserId);
    }

    if (effectiveInitials) {
      window.sessionStorage.setItem(USER_INITIALS_STORAGE_KEY, effectiveInitials);
    }

    if (effectiveUserId || effectiveInitials) {
      root.dataset.portalAccountType = storedAccountType;
      window.sessionStorage.setItem(ACCOUNT_TYPE_STORAGE_KEY, storedAccountType);
    } else {
      delete root.dataset.portalAccountType;
      window.sessionStorage.removeItem(ACCOUNT_TYPE_STORAGE_KEY);
    }

    root.dataset.userPortalSession = effectiveUserId || effectiveInitials ? 'live' : 'guest';
  }

  function setSessionState(root, userId, initials, accountType = USER_ACCOUNT_TYPE) {
    const normalizedUserId = String(userId || '').trim();
    const normalizedInitials = String(initials || '').trim();
    const clickEnabled = isAccountClickEnabled(root);

    if (normalizedUserId) {
      root.dataset.userPortalUserId = normalizedUserId;
      root.dataset.userPortalUserInitials = normalizedInitials;
      root.dataset.portalAccountType = accountType;
      root.dataset.userPortalSession = 'live';
      window.sessionStorage.setItem(USER_ID_STORAGE_KEY, normalizedUserId);
      window.sessionStorage.setItem(USER_INITIALS_STORAGE_KEY, normalizedInitials);
      window.sessionStorage.setItem(ACCOUNT_TYPE_STORAGE_KEY, accountType);
      window.ITMart24UserPortalSession = { live: true, userId: normalizedUserId, initials: normalizedInitials, accountType };
      window.dispatchEvent(new CustomEvent('itmart24:user-portal-session-changed', { detail: window.ITMart24UserPortalSession }));
      renderAccountState(root, normalizedInitials, clickEnabled);
      return;
    }

    delete root.dataset.userPortalUserId;
    delete root.dataset.userPortalUserInitials;
    delete root.dataset.portalAccountType;
    root.dataset.userPortalSession = 'guest';
    window.sessionStorage.removeItem(USER_ID_STORAGE_KEY);
    window.sessionStorage.removeItem(USER_INITIALS_STORAGE_KEY);
    window.sessionStorage.removeItem(ACCOUNT_TYPE_STORAGE_KEY);
    window.ITMart24UserPortalSession = { live: false, userId: '', initials: '', accountType: GUEST_ACCOUNT_TYPE };
    window.dispatchEvent(new CustomEvent('itmart24:user-portal-session-changed', { detail: window.ITMart24UserPortalSession }));
    renderAccountState(root, '', clickEnabled);
  }

  function attachAccountLinkHandlers(root) {
    const accountLinks = document.querySelectorAll(ACCOUNT_TRIGGER_SELECTOR);

    accountLinks.forEach((link) => {
      if (link.dataset.userPortalStrictHandlerBound === 'true') {
        return;
      }

      link.dataset.userPortalStrictHandlerBound = 'true';

      link.addEventListener(
        'click',
        (event) => {
          const sessionState = getSessionState(root);
          const loggedIn = sessionState.loggedIn;
          const clickEnabled = getEffectiveClickEnabled(root);
          const targetHref = getAccountTargetHref(root, sessionState);

          if (!clickEnabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          link.setAttribute('data-user-portal-account-href', targetHref);

          if (loggedIn) {
            event.preventDefault();
            const accountMenu = link.closest(ACCOUNT_MENU_SELECTOR);
            if (accountMenu) {
              accountMenu.open = false;
            }
            window.location.assign(targetHref);
          }
        },
        true
      );
    });
  }

  function attachAccountMenuHandlers() {
    const accountMenus = document.querySelectorAll(ACCOUNT_MENU_SELECTOR);

    document.addEventListener('click', (event) => {
      accountMenus.forEach((menu) => {
        if (!menu.contains(event.target)) {
          menu.open = false;
        }
      });
    });
  }

  async function hydrateAccountClickSetting(root) {
    const apiBase = root.dataset.adminApi || '';
    const storedValue = window.sessionStorage.getItem(ACCOUNT_CLICK_ENABLED_STORAGE_KEY);

    if (storedValue === 'true' || storedValue === 'false') {
      root.dataset.userPortalAccountClickEnabled = storedValue;
    } else {
      root.dataset.userPortalAccountClickEnabled = 'true';
    }

    renderAccountState(root, root.dataset.userPortalUserInitials || '', isAccountClickEnabled(root));

    if (!apiBase) {
      return;
    }

    try {
      const response = await fetch(`${apiBase.replace(/\/$/, '')}${PUBLIC_SETTINGS_PATH}?ts=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        return;
      }

      const payload = await response.json();
      const clickEnabled = payload?.data?.clickEnabled !== false;
      root.dataset.userPortalAccountClickEnabled = clickEnabled ? 'true' : 'false';
      window.sessionStorage.setItem(ACCOUNT_CLICK_ENABLED_STORAGE_KEY, clickEnabled ? 'true' : 'false');
      renderAccountState(root, root.dataset.userPortalUserInitials || '', clickEnabled);
    } catch (_error) {
      renderAccountState(root, root.dataset.userPortalUserInitials || '', isAccountClickEnabled(root));
    }
  }

  async function hydrateSession(root) {
    const apiBase = root.dataset.userPortalApi || '';
    if (!apiBase) {
      setSessionState(root, '', '');
      return;
    }

    try {
      const response = await fetch(`${apiBase.replace(/\/$/, '')}/auth/me`, {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        setSessionState(root, '', '');
        return;
      }

      const payload = await response.json();
      const fullName = payload?.data?.fullName || payload?.data?.full_name || '';
      const email = payload?.data?.email || '';
      const initials = buildInitials(fullName || email);
      setSessionState(root, payload?.data?.id || '', initials);
    } catch (_error) {
      setSessionState(root, '', '');
    }
  }

  function init() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    const storedAccountClickEnabled = window.sessionStorage.getItem(ACCOUNT_CLICK_ENABLED_STORAGE_KEY) || 'true';
    root.dataset.userPortalAccountClickEnabled = storedAccountClickEnabled;
    syncStoredSessionToRoot(root);
    attachAccountLinkHandlers(root);
    attachAccountMenuHandlers(root);

    if (root.dataset.userPortalUserInitials) {
      renderAccountState(root, root.dataset.userPortalUserInitials, getEffectiveClickEnabled(root));
    }

    window.addEventListener('itmart24:vendor-session-changed', (event) => {
      const detail = event?.detail || {};
      const vendorId = String(detail.vendorId || detail.userId || '').trim();
      const initials = String(detail.initials || '').trim();

      if (vendorId || initials) {
        root.dataset.vendorPortalVendorId = vendorId;
        root.dataset.vendorPortalVendorInitials = initials;
        root.dataset.vendorPortalSession = 'live';
        root.dataset.portalAccountType = VENDOR_ACCOUNT_TYPE;
        window.sessionStorage.setItem(USER_ID_STORAGE_KEY, vendorId);
        window.sessionStorage.setItem(USER_INITIALS_STORAGE_KEY, initials);
        window.sessionStorage.setItem(ACCOUNT_TYPE_STORAGE_KEY, VENDOR_ACCOUNT_TYPE);
        renderAccountState(root, initials, true);
        return;
      }

      delete root.dataset.vendorPortalVendorId;
      delete root.dataset.vendorPortalVendorInitials;
      root.dataset.vendorPortalSession = 'guest';
      setSessionState(root, '', '', GUEST_ACCOUNT_TYPE);
    });

    hydrateAccountClickSetting(root);
    hydrateSession(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
